// src/modules/app-views/_apps/appmulti/plans/pages/Document/tabs/CoveragesTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Card, Row, Col, Button } from 'antd';
import { useState, useEffect, useCallback } from 'react';

import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CoveragesTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const { form, locked } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const standardConfig = plansService.getCoverageConfig();
  const standardKeySet = new Set(standardConfig.map(c => c.value));
  const nonStandardOptions = (data?.coverages || [])
    .filter(c => c?.coverageKey && !standardKeySet.has(c.coverageKey))
    .reduce((acc, c) => {
      if (!acc.some(o => o.value === c.coverageKey)) {
        acc.push({ label: c.coverageKey, value: c.coverageKey });
      }
      return acc;
    }, []);
  const initialCoverageOptions = [
    ...standardConfig.map(c => ({ label: translateX(c.value), value: c.value })),
    ...nonStandardOptions,
  ];
  const currencyOptions = plansService.getCurrencies();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [coverages, setCoverages] = useState(data?.coverages || []);
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [coverageOptions, setCoverageOptions] = useState(initialCoverageOptions);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (data?.coverages?.length) {
      const currencies = {};
      data.coverages.forEach((c, i) => {
        currencies[i] = c.currency || 'USD';
      });
      setSelectedCurrency(currencies);
      setCoverages(data.coverages);
      form?.setFieldsValue?.({ coverages: data.coverages });
    }
    updateCoverageOptions(null, true);
  }, [data?.coverages]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateCoverageOptions = useCallback((selectedValue, firstValidation) => {
    const formCoverages = form?.getFieldValue(['coverages']) || [];
    const docCoverages = data?.coverages || [];

    const newOptions = initialCoverageOptions.map(c => ({
      ...c,
      disabled: !!(
        formCoverages.find(f => f?.coverageKey === c.value) ||
        (typeof firstValidation === 'boolean' &&
          firstValidation &&
          docCoverages.find(f => f?.coverageKey === c.value)) ||
        c.value === selectedValue
      ),
    }));

    setCoverageOptions([...newOptions]);
  }, [data?.coverages, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddCoverage = () => {
    const newCoverage = {
      coverageKey: '',
      type: 'money',
      amount: 0,
      currency: 'USD',
      included: true,
      text: '',
      observation: '',
      highlight: false,
    };
    const updated = [...coverages, newCoverage];
    const newIndex = updated.length - 1;
    setCoverages(updated);
    setSelectedCurrency(prev => ({ ...prev, [newIndex]: 'USD' }));
    form?.setFieldsValue?.({ coverages: updated });
  };

  const handleRemoveCoverage = index => {
    const updated = coverages.filter((_, i) => i !== index);
    const newCurrencies = {};
    updated.forEach((c, i) => {
      const origIndex = i >= index ? i + 1 : i;
      newCurrencies[i] = selectedCurrency[origIndex] || c.currency || 'USD';
    });
    setCoverages(updated);
    setSelectedCurrency(newCurrencies);
    form?.setFieldsValue?.({ coverages: updated });
    updateCoverageOptions();
  };

  const handleCoverageKeyChange = (index, value) => {
    const defaultObs = plansService.getCoverageDefaultObservation(value) ?? '';
    const updated = coverages.map((c, i) =>
      i === index ? { ...c, coverageKey: value, observation: defaultObs } : c,
    );
    setCoverages(updated);
    form?.setFieldValue(['coverages', index, 'observation'], defaultObs);
    updateCoverageOptions(value);
  };

  const handleCurrencyChange = (index, value) => {
    setSelectedCurrency(prev => ({ ...prev, [index]: value }));
    form?.setFieldValue(['coverages', index, 'currency'], value);
    const updated = coverages.map((c, i) =>
      i === index ? { ...c, currency: value } : c,
    );
    setCoverages(updated);
  };

  const handleValueChange = (index, field, value) => {
    const updated = coverages.map((c, i) =>
      i === index ? { ...c, [field]: value } : c,
    );
    setCoverages(updated);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      {/* Additional Information */}
      <Card title={translateX('additional_info_singular')}>
        <Row gutter={ROW_GUTTER} className="w-100">
          <Col span={24}>
            <SimpleTextArea name="coverageAdditionalInfo" noLabel rows={2} />
          </Col>
        </Row>
      </Card>

      {/* Coverages List */}
      <Card>
        {coverages.map((coverage, index) => {
          const hasObs =
            plansService.getCoverageHasObservation(coverage.coverageKey) &&
            (!!coverage.observation ||
              !!plansService.getCoverageDefaultObservation(coverage.coverageKey));
          const type = plansService.getCoverageType(coverage.coverageKey) || coverage?.type;
          const hasValue = (type === 'money' && coverage.amount != null) || type === 'text';
          const keySpan = hasObs ? (hasValue ? 10 : 16) : (hasValue ? 16 : 22);
          const obsSpan = 6;
          const valueSpan = 6;
          return (
            <Row gutter={ROW_GUTTER} key={index} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={keySpan}>
                <SimpleSelect
                  name={['coverages', index, 'coverageKey']}
                  noLabel
                  options={coverageOptions}
                  showSearch
                  allowClear
                  placeholder={translateX('coverage')}
                  onChange={value => handleCoverageKeyChange(index, value)}
                />
              </Col>

              {hasObs && (
                <Col xs={24} sm={obsSpan}>
                  <SimpleInput
                    name={['coverages', index, 'observation']}
                    noLabel
                    placeholder={translateX('observation')}
                    onChange={e => handleValueChange(index, 'observation', e?.target?.value ?? e)}
                  />
                </Col>
              )}

              {hasValue && (
                <Col xs={20} sm={valueSpan} className="text-center">
                  {renderDynamicField(
                    coverage,
                    index,
                    currencyOptions,
                    selectedCurrency,
                    handleCurrencyChange,
                    handleValueChange,
                  )}
                </Col>
              )}

              <Col
                xs={4}
                sm={2}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}
              >
                <Button
                  icon={<MinusCircleOutlined />}
                  disabled={locked}
                  onClick={() => handleRemoveCoverage(index)}
                />
              </Col>
            </Row>
          );
        })}

        <Button
          type="dashed"
          onClick={handleAddCoverage}
          disabled={locked || coverages.some(c => !isCoverageFilled(c))}
          block
          icon={<PlusOutlined />}
        >
          &nbsp;{translateX('add_coverage')}
        </Button>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CoveragesTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isCoverageFilled(coverage) {
  if (!coverage?.coverageKey) return false;
  const type = plansService.getCoverageType(coverage.coverageKey) || coverage?.type;
  if (!type) return true; // unknown type — no value field to fill
  switch (type) {
    case 'money': return coverage.amount === null || coverage.amount > 0;
    case 'switch': return true; // switch always has a value (true/false)
    case 'text': return !!coverage.text;
    default: return true;
  }
}

function renderDynamicField(coverage, index, currencyOptions, selectedCurrency, onCurrencyChange, onValueChange) {
  if (!coverage?.coverageKey) return null;

  const type = plansService.getCoverageType(coverage.coverageKey) || coverage?.type;

  if (!type) return null;

  switch (type) {
    case 'money':
      return (
        <SimpleNumber
          name={['coverages', index, 'amount']}
          noLabel
          min={0}
          decimalPlaces={2}
          suffix={currencyOptions}
          suffixValue={selectedCurrency[index] || coverage?.currency || 'USD'}
          onSuffixChange={value => onCurrencyChange(index, value)}
          onChange={value => onValueChange(index, 'amount', value)}
          x-suffix-style={{ width: 60 }}
        />
      );

    case 'text':
      return (
        <SimpleInput
          name={['coverages', index, 'text']}
          noLabel
          onChange={e => onValueChange(index, 'text', e?.target?.value ?? e)}
        />
      );

    default:
      return null;
  }
}
